"use client";

import * as React from "react";
import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAdminFeaturesExist } from "@/hooks/auth/use-admin-features-exist";
import { useHasRootPriviliges } from "@/hooks/auth/use-has-root-priviliges";
import { useCurrentEnterpriseCustomer } from "@/hooks/use-current-enterprise-customer";

import { useInitializePermissions } from "@/hooks/schema/use-initialize-permissions";
import { useEntityStore } from "@/stores/entity.store";
import { useEntitiesFromDb } from "@/hooks/schema/use-entities-from-db";

interface SettingsContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SettingsContext = React.createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = React.useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = React.useState(false);

  const { colors } = useCurrentEnterpriseCustomer();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "," && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        router.push("/p/settings/organization");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [router]);

  const { hasRootPriviliges: mayEdit, isLoading } = useHasRootPriviliges();

  const { allAdminFeaturesExist, isLoading: isLoadingAdminExists } = useAdminFeaturesExist();

  // Auth redirect is now handled by middleware (proxy.ts)
  // Only handle feature management redirect here
  useEffect(() => {
    if (!pathname.includes("/p/feature-management")) {
      if (!allAdminFeaturesExist && !isLoadingAdminExists && mayEdit && !isLoading) {
        router.push("/p/feature-management");
      }
    }
  }, [pathname, allAdminFeaturesExist, isLoadingAdminExists, mayEdit, router, isLoading]);

  useEffect(() => {
    if (colors && colors.secondary) {
      document.documentElement.style.setProperty("--secondary", colors.secondary);
      document.documentElement.style.setProperty("--accent", colors.accent);
    }
  }, [colors]);

  const { setEntityList } = useEntityStore();
  const { entities } = useEntitiesFromDb();
  useEffect(() => {
    if (entities) {
      setEntityList(entities);
    }
  }, [entities, setEntityList]);

  // We should only use this here. We fetch, refetch and parse the permissions and write them to a zustand store.
  // the rest of the application can then consume from the zustand store
  useInitializePermissions();

  return (
    <SettingsContext.Provider value={{ isOpen, setIsOpen }}>{children}</SettingsContext.Provider>
  );
};
